import { reactive, UnwrapNestedRefs } from 'vue';
import EventBus from './EventBus';
import { GameEvents } from './GameEvents';
import ProgressManager from './ProgressManager';
import getLogger from './logger';
import { ProductionLevelDataService, type LevelDataService } from './services/LevelDataService';

import GameplayHandler from './handlers/GameplayHandler';
import ProgressHandler from './handlers/ProgressHandler';
import TutorialHandler from './handlers/TutorialHandler';
import MenuHandler from './handlers/MenuHandler';
import AnalyticsHandler from './handlers/AnalyticsHandler';
import LevelHandler from './handlers/LevelHandler';
import SpatialHandler from './handlers/SpatialHandler';
import SolutionReplayHandler from './handlers/SolutionReplayHandler';
import ExamineHandler from './handlers/ExamineHandler';

const logger = getLogger('GameManager');

const isDev = process.env.NODE_ENV === 'development';

// === Type Definitions ===

type ScreenState = 'home' | 'playing' | 'win' | 'lose' | 'level_win';

export interface LevelState {
  failures: number;
  tutorialShown: boolean;
  bestPerfect: { moveCount: number } | null;
  bestAssisted: { moveCount: number; undoCount: number } | null;
}

export interface UiState {
  screen: ScreenState;
  activePlayMode: 'normal' | 'replay';

  gameplay: {
    currentLevelKey: string | null;
    levelWinData: {
      currentMoveCount: number;
      currentUndoCount: number;
      bestPlayerMoveCount: number | null;
      bestSolutionMoveCount: number | null;
    } | null;
    actionModes: string[];
    actionModeStates: Record<string, unknown>;
    currentActionMode: string;
    currentMoveCount: number;
    currentUndoCount: number;
    lastCompletedLevelSolution: any[] | null;
    optimalMoveCounts: Record<string, number>;
  };

  progress: {
    levelStates: Record<string, LevelState>;
    hasAnyProgress: boolean;
    gameComplete: boolean;
    nextUnbeatenLevel: string | null;
    unlockedLevelIds: Record<string, boolean>;
    completedLevelIds: Record<string, boolean>;
    claimedRewards: Record<string, boolean>;
    completedChapterReward: {
      reward: { name: string; component: string; description: string };
      campaignId: string;
      campaignName: string;
    } | null;
  };

  tutorial: {
    currentTutorial: { text: string; imageUrl: string | null } | null;
    availableTutorial: { text: string; image?: string; failures?: number } | null;
  };

  menu: Record<string, unknown>;

  examine: {
    examinedEnemyId: number | null;
    enemyName: string;
    displayInfos: Array<{ icon: string; label: string; description: string }>;
    expanded: boolean;
  };

  spatial: {
    terrain: Array<{
      entityId: string;
      coords: { q: number; r: number };
      spriteKey: string;
    }>;
    characters: Array<{
      entityId: string;
      coords: { q: number; r: number };
      bearing: number | null;
      spriteKey: string;
      scale: number;
      isDead: boolean;
      isPlayer: boolean;
      energy: number | null;
      predictedEnergy: number | null;
      speed: number | null;
    }>;
    pendingAnimations: {
      plan: any[][];
      resolve: (() => void) | null;
    } | null;
    highlights: {
      playerActions: Array<{ q: number; r: number }>;
      enemyPattern: Array<{ q: number; r: number }> | null;
    };
    intentionIndicators: Array<{
      entityId: string;
      position: { q: number; r: number };
      bearing: number;
      actionType: string;
      details: {
        coords?: { q: number; r: number };
        bearing?: number;
        targetId?: string;
        sourceComponentName?: string;
      };
    }>;
  };

  solutionReplay: {
    currentStepIndex: number;
    totalSteps: number;
    playerSolution: any[];
    canStepBackward: boolean;
    canStepForward: boolean;
    replayCompleted: boolean;
  };

  curation?: {
    currentCandidateId: string | null;
    candidateManifest: any | null;
    currentCandidateIndex: number;
    levelEnded: 'win' | 'lose' | null;
  };

  devSolutionReplay?: {
    solutionCount: number;
    isPlayerTurn: boolean;
  };
}

// === GameManager ===

export default class GameManager {
  eventBus: EventBus;
  uiState: UnwrapNestedRefs<UiState>;
  progressManager: ProgressManager;
  private progressHandler: ProgressHandler;

  constructor(levelDataService?: LevelDataService) {
    this.eventBus = new EventBus();
    this.progressManager = new ProgressManager();

    const state: UiState = {
      screen: 'home',
      activePlayMode: 'normal',

      gameplay: {
        currentLevelKey: null,
        levelWinData: null,
        actionModes: [],
        actionModeStates: {},
        currentActionMode: 'STANDARD',
        currentMoveCount: 0,
        currentUndoCount: 0,
        lastCompletedLevelSolution: null,
        optimalMoveCounts: {},
      },

      progress: {
        levelStates: {},
        hasAnyProgress: false,
        gameComplete: false,
        nextUnbeatenLevel: null,
        unlockedLevelIds: {},
        completedLevelIds: {},
        claimedRewards: {},
        completedChapterReward: null,
      },

      tutorial: {
        currentTutorial: null,
        availableTutorial: null,
      },

      menu: {},

      examine: {
        examinedEnemyId: null,
        enemyName: '',
        displayInfos: [],
        expanded: false,
      },

      spatial: {
        terrain: [],
        characters: [],
        pendingAnimations: null,
        highlights: {
          playerActions: [],
          enemyPattern: null,
        },
        intentionIndicators: [],
      },

      solutionReplay: {
        currentStepIndex: 0,
        totalSteps: 0,
        playerSolution: [],
        canStepBackward: false,
        canStepForward: false,
        replayCompleted: false,
      },
    };

    if (isDev) {
      state.curation = {
        currentCandidateId: null,
        candidateManifest: null,
        currentCandidateIndex: 0,
        levelEnded: null,
      };
      state.devSolutionReplay = {
        solutionCount: 0,
        isPlayerTurn: false,
      };
    }

    this.uiState = reactive<UiState>(state);

    // Screen updates live here — every handler that wants to change screen emits SET_SCREEN
    this.eventBus.on(GameEvents.SET_SCREEN, (screen: ScreenState) => {
      this.uiState.screen = screen;
    });

    // Create data service for level/solution loading (or use injected service for tests)
    const dataService = levelDataService || new ProductionLevelDataService();

    // Dev handlers FIRST — their dispatch listeners must be registered before production
    // handlers so they can consume LEVEL_WON / LEVEL_LOST / RESTART_GAME.
    if (isDev) {
      const CurationMode = require('./dev/CurationMode').default;
      const SolutionReplayMode = require('./dev/SolutionReplayMode').default;
      new CurationMode(this.uiState, this.uiState.curation!, this.eventBus);
      new SolutionReplayMode(this.uiState, this.uiState.devSolutionReplay!, this.eventBus, dataService);
    }

    // Production handlers (order matters - replay intercepts first)
    new SolutionReplayHandler(this.uiState, this.uiState.solutionReplay, this.eventBus);
    new GameplayHandler(this.uiState, this.uiState.gameplay, this.eventBus, dataService);
    this.progressHandler = new ProgressHandler(this.uiState, this.uiState.progress, this.eventBus, this.progressManager);
    new TutorialHandler(this.uiState, this.uiState.tutorial, this.eventBus);
    new MenuHandler(this.uiState, this.uiState.menu, this.eventBus);
    new ExamineHandler(this.uiState, this.uiState.examine, this.eventBus);
    new SpatialHandler(this.uiState, this.uiState.spatial, this.eventBus);
    new AnalyticsHandler(this.uiState, this.eventBus);
    new LevelHandler(this.uiState, this.eventBus, dataService);
  }

  async init(): Promise<void> {
    await this.progressManager.init();
    this.progressHandler.init();

    // Analytics player identification
    try {
      const { playerIdManager, identifyPlayer } = require('./analytics');
      await playerIdManager.init(this.progressManager.storage);
      const playerId = await playerIdManager.getPlayerId();
      identifyPlayer(playerId);
    } catch (e) {
      logger.warn('Analytics init failed:', e);
    }
  }
}
